import React, { useCallback, useEffect, useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { DATE_DISPLAY_FORMAT, displayDate } from "../../utils/dateUtils";
import { isValid as isValidDate } from "date-fns";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Button,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Paper,
  CircularProgress,
  Rating,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  Grade as GradeIcon,
  Search as SearchIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import departmentService from "../../services/departmentService";
import assignmentService from "../../services/assignmentService";
import courseService from "../../services/courseService";
import { downloadBlob } from "../../utils/download";

dayjs.extend(isSameOrBefore);


/**
 * Coerce a date-like value into a VALID native Date for the MUI DatePicker
 * (app uses AdapterDateFns). Returns null for missing/unparseable values so the
 * picker never receives an "Invalid Date", which makes AdapterDateFns throw
 * "Invalid time value" and crashes the page via the error boundary.
 */
const toValidDate = (value) => {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return isValidDate(d) ? d : null;
};

const normalizeOptions = (items, fallback = []) => {
  if (!Array.isArray(items)) return fallback;
  const mapped = items
    .map((item) => {
      if (item && typeof item === "object") {
        const id =
          item.id ??
          item.value ??
          item._id ??
          item.code ??
          item.classId ??
          item.ClassId ??
          item.courseId ??
          item.CourseId ??
          item.subjectId ??
          item.SubjectId ??
          item.departmentId ??
          item.DepartmentId ??
          item.sectionId ??
          item.uuid ??
          item.name ??
          item.className ??
          item.ClassName ??
          item.subjectName ??
          item.SubjectName ??
          item.courseName ??
          item.CourseName ??
          item.departmentName ??
          item.DepartmentName;

        const baseLabel=item.name||item.title||item.label||item.code||item.className||item.ClassName||item.subjectName||item.SubjectName||item.courseName||item.CourseName||item.departmentName||item.DepartmentName||item.sectionName;
        const section=item.section||item.sectionName||item.Section||item.division||item.Division;
        const label=section&&baseLabel&&!String(baseLabel).includes(String(section))?`${baseLabel} - ${section}`:baseLabel;
        if (!id || !label) return null;
        return { id: String(id), name: String(label) };
      }
      if (typeof item === "string" || typeof item === "number") {
        return { id: String(item), name: String(item) };
      }
      return null;
    })
    .filter(Boolean);

  if (mapped.length === 0) return fallback;
  return Array.from(new Map(mapped.map((opt) => [opt.id, opt])).values());
};

const getInitials = (name) => {
  if (!name) return "NA";
  const initials = name
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  return initials.slice(0, 2) || "NA";
};

const normalizeStatus = (status) => {
  const rawValue = (status || "").toString().trim();
  if (!rawValue) return "submitted";
  const value = rawValue.toLowerCase();
  if (value.includes("reject")) return "rejected";
  if (value.includes("grade") || value.includes("approve")) return "graded";
  if (value.includes("pending")) return "submitted";
  if (value.includes("submit")) return "submitted";
  return value;
};

const sanitizeParam = (value) => {
  if (!value) return undefined;
  return String(value).replace(/'/g, "");
};

const computeCounts = (list = []) => {
  return list.reduce(
    (acc, item) => {
      const normalizedStatus = normalizeStatus(item?.status);
      if (normalizedStatus === "submitted") {
        acc.submitted += 1;
      } else if (normalizedStatus === "graded") {
        acc.graded += 1;
      } else if (normalizedStatus === "rejected") {
        acc.rejected += 1;
      }
      return acc;
    },
    { submitted: 0, graded: 0, rejected: 0 }
  );
};

const emptyCounts = { submitted: 0, graded: 0, rejected: 0 };

const normalizeCounts = (counts, fallback) => {
  if (!counts) return fallback;
  return {
    submitted: Number(counts.submitted ?? fallback.submitted ?? 0),
    graded: Number(counts.graded ?? fallback.graded ?? 0),
    rejected: Number(counts.rejected ?? fallback.rejected ?? 0),
  };
};

const extractArray = (raw) => {
  if (Array.isArray(raw)) return raw;

  const candidates = [
    raw?.submissions,
    raw?.data,
    raw?.rows,
    raw?.items,
    raw?.list,
    raw?.results,
    raw?.records,
    raw?.values,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
    if (candidate && typeof candidate === "object") {
      const nested = extractArray(candidate);
      if (nested.length > 0) {
        return nested;
      }
    }
  }

  return [];
};

const normalizeSubmission = (item, index) => {
  const rawStudent =
    item?.student ||
    item?.Student ||
    item?.studentDetails ||
    item?.studentInfo ||
    item?.studentData ||
    {};

  const studentUser = rawStudent?.user || rawStudent?.User || {};

  const assignment =
    item?.assignment ||
    item?.Assignment ||
    item?.assignmentDetails ||
    item?.assignmentData ||
    {};

  const subject =
    item?.subject ||
    assignment?.subject ||
    assignment?.Subject ||
    item?.Subject ||
    {};

  const course =
    item?.course || assignment?.course || assignment?.Course || item?.Course || {};

  const classInfo =
    item?.class ||
    item?.Class ||
    item?.classInfo ||
    item?.classDetails ||
    assignment?.class ||
    assignment?.Class || {};

  const department =
    item?.department || assignment?.department || assignment?.Department || item?.Department || {};
  const submissionMeta = item?.submission || {};
  const attachments =
    item?.attachments ||
    submissionMeta?.attachments ||
    item?.files ||
    submissionMeta?.files ||
    [];

  const status = normalizeStatus(
    item?.status || item?.reviewStatus || submissionMeta?.status || item?.submissionStatus
  );

  const submittedOn =
    item?.submittedOn ||
    item?.submittedAt ||
    submissionMeta?.submittedAt ||
    item?.submissionDate ||
    null;

  const dueDate = assignment?.dueDate || item?.dueDate || null;

  const departmentId =
    department?.id ||
    department?.departmentId ||
    item?.departmentId ||
    assignment?.departmentId ||
    rawStudent?.departmentId ||
    null;

  const courseId =
    course?.id || course?.courseId || item?.courseId || assignment?.courseId || null;

  const classId =
    classInfo?.id || classInfo?.classId || item?.classId || assignment?.classId || null;

  const subjectId =
    subject?.id || subject?.subjectId || item?.subjectId || assignment?.subjectId || null;

  const studentName =
    rawStudent?.name ||
    rawStudent?.fullName ||
    studentUser?.fullName ||
    studentUser?.name ||
    item?.studentName ||
    item?.student_full_name ||
    "Unknown";

  const pickDownloadUrl = (entry) => {
    if (!entry) return "";
    if (typeof entry === "string") {
      const s = entry.trim();
      return s.startsWith("http://") || s.startsWith("https://") ? s : "";
    }
    const candidates = [entry.url, entry.signedUrl, entry.fileUrl, entry.downloadUrl].filter(Boolean);
    for (const c of candidates) {
      const s = String(c).trim();
      if (s.startsWith("http://") || s.startsWith("https://")) return s;
    }
    return "";
  };

  const fileEntry = Array.isArray(attachments) && attachments.length > 0
    ? attachments.find((a) => pickDownloadUrl(a)) || attachments[0]
    : item?.file || item?.attachment || assignment?.attachment || null;

  const rawMaxMarks =
    assignment?.maxMarks ??
    item?.maxMarks ??
    submissionMeta?.maxMarks ??
    item?.totalMarks ??
    null;

  let maxMarks = null;
  if (rawMaxMarks !== undefined && rawMaxMarks !== null && rawMaxMarks !== "") {
    if (typeof rawMaxMarks === "number") {
      maxMarks = rawMaxMarks;
    } else {
      const parsed = Number(rawMaxMarks);
      maxMarks = Number.isNaN(parsed) ? rawMaxMarks : parsed;
    }
  }

  const rawMaxGrades =
    assignment?.maxGrades ??
    item?.maxGrades ??
    submissionMeta?.maxGrades ??
    null;

  const maxGrades =
    rawMaxGrades === undefined || rawMaxGrades === null || rawMaxGrades === ""
      ? null
      : String(rawMaxGrades).toUpperCase();

  const evaluationType =
    assignment?.evaluationType ??
    item?.evaluationType ??
    submissionMeta?.evaluationType ??
    (maxGrades ? "grade" : "marks");

  const grade = item?.grade ?? item?.score ?? item?.marksObtained ?? null;
  const parsedMaxScore =
    (typeof maxMarks === "number" ? maxMarks : null) ??
    assignment?.maxScore ??
    item?.maxScore ??
    item?.totalMarks ??
    100;
  const maxGrade = typeof parsedMaxScore === "string" ? parseFloat(parsedMaxScore) || 100 : parsedMaxScore;

  const normalizedEvaluationType =
    typeof evaluationType === "string" ? evaluationType.toLowerCase() : "";

  const hasMarksValue =
    maxMarks !== null &&
    maxMarks !== undefined &&
    String(maxMarks).trim() !== "" &&
    !Number.isNaN(Number(maxMarks));

  const hasGradesValue =
    maxGrades !== null &&
    maxGrades !== undefined &&
    String(maxGrades).trim() !== "";

  const isMarksEvaluation =
    normalizedEvaluationType === "marks" ||
    (normalizedEvaluationType !== "grade" && hasMarksValue);

  const isGradeEvaluation =
    normalizedEvaluationType === "grade" || (!isMarksEvaluation && hasGradesValue);

  return {
    id: item?.id || item?.submissionId || item?.assignmentSubmissionId || `submission-${index}`,
    assignmentId:
      assignment?.id || item?.assignmentId || item?.assignment_id || `assignment-${index}`,
    assignmentTitle:
      assignment?.title || assignment?.name || item?.assignmentTitle || item?.assignmentName || "Assignment",
    studentId: rawStudent?.id || item?.studentId || item?.student_id || `student-${index}`,
    studentName,
    studentAvatar: rawStudent?.avatar || studentUser?.avatar || getInitials(studentName),
    rollNo:
      rawStudent?.rollNo ||
      rawStudent?.rollNumber ||
      rawStudent?.registrationNumber ||
      studentUser?.rollNo ||
      studentUser?.registrationNumber ||
      item?.rollNo ||
      item?.studentRollNo ||
      "",
    class: classInfo?.name || classInfo?.className || item?.className || "-",
    classId: classId ? String(classId) : null,
    subject:
      subject?.name ||
      subject?.title ||
      item?.subjectName ||
      assignment?.subjectName ||
      assignment?.name ||
      "-",
    subjectId: subjectId ? String(subjectId) : null,
    course: course?.name || course?.title || item?.courseName || assignment?.courseName || "-",
    courseId: courseId ? String(courseId) : null,
    department: department?.name || item?.departmentName || assignment?.departmentName || "-",
    departmentId: departmentId ? String(departmentId) : null,
    submittedOn,
    dueDate,
    status,
    file:
      (typeof fileEntry === "string" && fileEntry) ||
      fileEntry?.name ||
      fileEntry?.fileName ||
      "-",
    fileUrl:
      pickDownloadUrl(fileEntry) ||
      pickDownloadUrl(attachments?.[0]) ||
      pickDownloadUrl(assignment?.attachments?.[0]) ||
      pickDownloadUrl(assignment?.attachment) ||
      pickDownloadUrl(item?.assignment?.attachments?.[0]) ||
      pickDownloadUrl(item?.assignment?.attachment) ||
      (item?.assignment?.attachmentUrl && String(item.assignment.attachmentUrl).startsWith("http")
        ? item.assignment.attachmentUrl
        : "") ||
      (item?.fileUrl && String(item.fileUrl).startsWith("http") ? item.fileUrl : "") ||
      (item?.attachmentUrl && String(item.attachmentUrl).startsWith("http") ? item.attachmentUrl : "") ||
      (submissionMeta?.fileUrl && String(submissionMeta.fileUrl).startsWith("http") ? submissionMeta.fileUrl : "") ||
      (submissionMeta?.attachmentUrl && String(submissionMeta.attachmentUrl).startsWith("http")
        ? submissionMeta.attachmentUrl
        : "") ||
      "",
    fileSize: fileEntry?.size ? `${(fileEntry.size / (1024 * 1024)).toFixed(1)} MB` : "-",
    onTime:
      item?.onTime ??
      (submittedOn && dueDate ? dayjs(submittedOn).isSameOrBefore(dayjs(dueDate)) : true),
    grade,
    maxGrade,
    maxMarks,
    maxGrades,
    evaluationType,
    gradingMeta: {
      isMarksEvaluation,
      isGradeEvaluation,
      maxMarksValue: hasMarksValue ? Number(maxMarks) : null,
      maxGradesValue: hasGradesValue ? String(maxGrades).toUpperCase() : null,
    },
    attemptCount:
      item?.attemptCount ??
      item?.attempt_count ??
      submissionMeta?.attemptCount ??
      submissionMeta?.attempt_count ??
      1,
    feedback: item?.feedback || submissionMeta?.feedback || "",
  };
};

/** Second line under student name (roll | class); skips empty values and "-" placeholders */
const submissionViewSubtitle = (submission) => {
  if (!submission) return "";
  const roll = String(submission.rollNo ?? "").trim();
  const cls = String(submission.class ?? "").trim();
  const parts = [];
  if (roll && roll !== "-") parts.push(roll);
  if (cls && cls !== "-") parts.push(cls);
  return parts.join(" | ");
};

function AssignmentApproval() {
  const [tabValue, setTabValue] = useState(0);
  const [submissions, setSubmissions] = useState([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [gradeInput, setGradeInput] = useState("");
  const [feedback, setFeedback] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [extendedDueDate, setExtendedDueDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [courseFilter, setCourseFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const notify = useCallback((message, severity = "success") => {
    const msg = String(message || "").trim();
    if (!msg) return;
    setToast({ open: true, message: msg, severity });
  }, []);

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [courseLoading, setCourseLoading] = useState(false);
  const [classOptions, setClassOptions] = useState([]);
  const [classLoading, setClassLoading] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [filters, setFilters] = useState({
    departmentId: "",
    courseId: "",
    classId: "",
    subjectId: "",
  });
  const [submissionCounts, setSubmissionCounts] = useState(() =>
    normalizeCounts(null, emptyCounts)
  );
  const [actionLoading, setActionLoading] = useState(false);
  
  const downloadSubmissionFile = useCallback(async (submission) => {
    const url = String(submission?.fileUrl ?? "").trim();
    const rawName = submission?.file;
    const filename =
      rawName && String(rawName).trim() && String(rawName).trim() !== "-"
        ? String(rawName).replace(/[/\\?%*:|"<>]/g, "_")
        : "submission";

    if (!url || url === "-" || url.toLowerCase() === "undefined") {
      notify("No file available to download.", "warning");
      return;
    }

    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      downloadBlob(blob, filename, blob.type || "application/octet-stream");
      notify("Download started.", "success");
    } catch {
      try {
        const w = window.open(url, "_blank", "noopener,noreferrer");
        if (!w) {
          notify("Unable to download. Allow pop-ups or try again.", "error");
        }
      } catch {
        notify("Unable to download submission.", "error");
      }
    }
  }, [notify]);

  const handleDownloadSubmission = useCallback(() => {
    downloadSubmissionFile(selectedSubmission);
  }, [selectedSubmission, downloadSubmissionFile]);


  const fetchSubmissions = useCallback(async () => {
    const statusParam =
      tabValue === 0
        ? "submitted"
        : tabValue === 1
        ? "graded"
        : tabValue === 2
        ? "rejected"
        : "";

    const params = {
      departmentId: sanitizeParam(filters.departmentId),
      courseId: sanitizeParam(filters.courseId),
      classId: sanitizeParam(filters.classId),
      subjectId: sanitizeParam(filters.subjectId),
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      status: statusParam,
    };

    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    const response = await assignmentService.getStudentsSubmittedAssignments(params);
    const payload = response?.data?.data ?? response?.data ?? {};
    const submissionsArray = extractArray(payload?.submissions ?? payload);

    const normalized = submissionsArray.map((item, index) =>
      normalizeSubmission(item, index)
    );

    const countsCandidate =
      response?.data?.counts ||
      response?.data?.data?.counts ||
      payload?.counts ||
      response?.counts;
    const fallbackCounts = computeCounts(normalized);
    const normalizedCounts = normalizeCounts(countsCandidate, fallbackCounts);

    const pagination =
      response?.data?.pagination ||
      response?.data?.data?.pagination ||
      payload?.pagination ||
      response?.pagination;
    const totalItemsRaw =
      pagination?.totalItems ??
      pagination?.total ??
      pagination?.rowCount ??
      countsCandidate?.total ??
      response?.data?.totalItems ??
      response?.totalItems;
    const totalItems = Number(totalItemsRaw) > 0 ? Number(totalItemsRaw) : normalized.length;

    const subjectOptionsCandidate = normalized
      .map((item) => ({ id: item.subjectId, name: item.subject }))
      .filter((option) => option.id && option.name);

    return {
      submissions: normalized,
      counts: normalizedCounts,
      totalItems,
      subjectOptionsCandidate,
    };
  }, [
    filters.departmentId,
    filters.courseId,
    filters.classId,
    filters.subjectId,
    paginationModel.page,
    paginationModel.pageSize,
    tabValue,
  ]);

  const handleFilterChange = useCallback((key, value) => {
    const normalizedValue = value === "all" ? "" : value;

    setFilters((prev) => {
      const next = { ...prev, [key]: normalizedValue };

      if (key === "departmentId") {
        next.courseId = "";
        next.classId = "";
        next.subjectId = "";
        setCourseFilter("all");
        setClassFilter("all");
        setSubjectFilter("all");
      } else if (key === "courseId") {
        next.classId = "";
        next.subjectId = "";
        setClassFilter("all");
        setSubjectFilter("all");
      } else if (key === "classId") {
        next.subjectId = "";
        setSubjectFilter("all");
      }

      setPaginationModel((prevModel) => ({ ...prevModel, page: 0 }));
      return next;
    });
  }, [setCourseFilter, setClassFilter, setSubjectFilter]);

  useEffect(() => {
    let isMounted = true;

    const fetchDepartments = async () => {
      setDepartmentLoading(true);
      try {
        const response = await departmentService.getDepartmentNames();
        const departmentsData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setDepartmentOptions(
          normalizeOptions(departmentsData, [])
        );
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch departments:", error);
          setDepartmentOptions([]);
        }
      } finally {
        if (isMounted) {
          setDepartmentLoading(false);
        }
      }
    };

    fetchDepartments();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchCourses = async () => {
      if (!filters.departmentId) {
        setCourseOptions([]);
        setCourseLoading(false);
        return;
      }

      setCourseLoading(true);
      try {
        const params = {
          id: sanitizeParam(filters.departmentId),
          departmentId: sanitizeParam(filters.departmentId),
        };

        const response = await assignmentService.getAssignmentCourseNames(params);
        const coursesDataRaw = response?.data?.data || response?.data || [];
        const coursesArray = Array.isArray(coursesDataRaw)
          ? coursesDataRaw
          : Array.isArray(coursesDataRaw?.courses)
          ? coursesDataRaw.courses
          : [];
        if (!isMounted) return;
        setCourseOptions(normalizeOptions(coursesArray, []));
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch courses:", error);
          setCourseOptions([]);
        }
      } finally {
        if (isMounted) {
          setCourseLoading(false);
        }
      }
    };

    fetchCourses();

    return () => {
      isMounted = false;
    };
  }, [filters.departmentId]);

  useEffect(() => {
    let isMounted = true;

    const fetchClasses = async () => {
      if (!filters.courseId) {
        setClassOptions([]);
        setClassLoading(false);
        return;
      }

      setClassLoading(true);
      try {
        const response = await courseService.getCourseClasses({
          id: sanitizeParam(filters.courseId),
        });
        const classesData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setClassOptions(normalizeOptions(classesData, []));
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch classes:", error);
          setClassOptions([]);
        }
      } finally {
        if (isMounted) {
          setClassLoading(false);
        }
      }
    };

    fetchClasses();

    return () => {
      isMounted = false;
    };
  }, [filters.courseId]);

  useEffect(() => {
    let isMounted = true;

    const fetchSubjects = async () => {
      if (!filters.classId) {
        setSubjectOptions([]);
        setSubjectLoading(false);
        return;
      }

      setSubjectLoading(true);
      try {
        const params = {
          id: sanitizeParam(filters.classId),
        };

        if (filters.courseId) {
          params.courseId = sanitizeParam(filters.courseId);
        }

        if (filters.departmentId) {
          params.departmentId = sanitizeParam(filters.departmentId);
        }

        const response = await assignmentService.getAssignmentSubjectNames(params);
        const subjectsDataRaw = response?.data?.data || response?.data || [];
        const subjectsArray = Array.isArray(subjectsDataRaw)
          ? subjectsDataRaw
          : Array.isArray(subjectsDataRaw?.subjects)
          ? subjectsDataRaw.subjects
          : [];
        if (!isMounted) return;
        setSubjectOptions(normalizeOptions(subjectsArray, []));
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch subjects:", error);
          setSubjectOptions([]);
        }
      } finally {
        if (isMounted) {
          setSubjectLoading(false);
        }
      }
    };

    fetchSubjects();

    return () => {
      isMounted = false;
    };
  }, [filters.classId,filters.courseId,filters.departmentId]);

  useEffect(() => {
    let isActive = true;

    const loadSubmissions = async () => {
      setSubmissionsLoading(true);
      try {
        const result = await fetchSubmissions();
        if (!isActive) return;
        setSubmissions(result.submissions);
        setSubmissionCounts(result.counts);
        setTotalSubmissions(result.totalItems);
        if (result.subjectOptionsCandidate.length > 0) {
          setSubjectOptions((prev) =>
            normalizeOptions(result.subjectOptionsCandidate, prev)
          );
        }
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to fetch submissions:", error);
        setSubmissions([]);
        setSubmissionCounts(normalizeCounts(null, emptyCounts));
        setTotalSubmissions(0);
      } finally {
        if (isActive) {
          setSubmissionsLoading(false);
        }
      }
    };

    loadSubmissions();

    return () => {
      isActive = false;
    };
  }, [fetchSubmissions]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [tabValue]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchQuery]);

  const getFilteredSubmissions = () => {
    if (!searchQuery) return submissions;

    const query = searchQuery.toLowerCase();
    return submissions.filter(
      (s) =>
        s.studentName.toLowerCase().includes(query) ||
        s.rollNo.toLowerCase().includes(query)
    );
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setViewDialogOpen(true);
  };

  const handleOpenGradeDialog = (submission) => {
    setSelectedSubmission(submission);
    const existingValue =
      submission && submission.grade !== undefined && submission.grade !== null
        ? submission.grade
        : "";
    setGradeInput(
      existingValue !== "" ? String(existingValue) : ""
    );
    setFeedback("");
    setGradeDialogOpen(true);
  };

  const handleOpenRejectDialog = (submission) => {
    setSelectedSubmission(submission);
    setRejectionReason("");
    setExtendedDueDate(toValidDate(submission?.dueDate));
    setRejectDialogOpen(true);
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectionReason("");
    setExtendedDueDate(null);
  };

  const getSubmissionEvaluationMeta = (submission) => {
    if (!submission) {
      return {
        isMarksEvaluation: false,
        isGradeEvaluation: false,
        maxMarksValue: null,
        maxGradesValue: null,
      };
    }

    if (submission.gradingMeta) {
      return {
        isMarksEvaluation: submission.gradingMeta.isMarksEvaluation,
        isGradeEvaluation: submission.gradingMeta.isGradeEvaluation,
        maxMarksValue: submission.maxMarks ?? submission.maxGrade ?? null,
        maxGradesValue: submission.maxGrades ?? null,
      };
    }

    const rawMaxMarks = submission.maxMarks;
    const hasMarks =
      rawMaxMarks !== undefined &&
      rawMaxMarks !== null &&
      String(rawMaxMarks).trim() !== "";

    const rawMaxGrades = submission.maxGrades;
    const hasGrades =
      !hasMarks &&
      rawMaxGrades !== undefined &&
      rawMaxGrades !== null &&
      String(rawMaxGrades).trim() !== "";

    return {
      isMarksEvaluation: hasMarks,
      isGradeEvaluation: hasGrades,
      maxMarksValue: hasMarks ? rawMaxMarks : null,
      maxGradesValue: hasGrades ? rawMaxGrades : null,
    };
  };

  const handleGradeSubmission = async () => {
    if (!selectedSubmission) return;

    const { isMarksEvaluation, isGradeEvaluation } =
      getSubmissionEvaluationMeta(selectedSubmission);

    const trimmedInput = (gradeInput ?? "").trim();

    if (trimmedInput === "") {
      return;
    }

    const payload = { status: "graded" };
    const trimmedFeedback = feedback.trim();
    if (trimmedFeedback) {
      payload.feedback = trimmedFeedback;
    }

    if (isMarksEvaluation) {
      const numericScore = Number(trimmedInput);
      if (Number.isNaN(numericScore)) {
        notify("Please enter a valid numeric score.", "warning");
        return;
      }
      payload.score = numericScore;
    } else if (isGradeEvaluation) {
      const singleLetterGrade = trimmedInput.toUpperCase();
      if (!/^[A-Z]$/.test(singleLetterGrade)) {
        notify("Please enter a single letter grade (A-Z).", "warning");
        return;
      }
      payload.grade = singleLetterGrade;
    } else {
      const numericScore = Number(trimmedInput);
      if (!Number.isNaN(numericScore)) {
        payload.score = numericScore;
      } else {
        payload.grade = trimmedInput;
      }
    }

    try {
      setActionLoading(true);
      await assignmentService.gradeSubmission(selectedSubmission.id, payload);
      const result = await fetchSubmissions();
      setSubmissions(result.submissions);
      setSubmissionCounts(result.counts);
      setTotalSubmissions(result.totalItems);
      if (result.subjectOptionsCandidate.length > 0) {
        setSubjectOptions((prev) =>
          normalizeOptions(result.subjectOptionsCandidate, prev)
        );
      }
      setGradeDialogOpen(false);
      setSelectedSubmission(null);
      setGradeInput("");
      setFeedback("");
      notify("Assignment graded successfully!", "success");
    } catch (error) {
      console.error("Failed to grade submission:", error);
      notify(
        error?.response?.data?.message || error?.message || "Failed to grade submission. Please try again.",
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmission = async () => {
    if (!selectedSubmission || !rejectionReason || !extendedDueDate) return;

    const dueDateObj =
      extendedDueDate instanceof Date ? extendedDueDate : new Date(extendedDueDate);
    if (isNaN(dueDateObj.getTime())) {
      notify("Please select a valid extended due date.", "error");
      return;
    }

    try {
      setActionLoading(true);
      await assignmentService.gradeSubmission(selectedSubmission.id, {
        status: "rejected",
        feedback: rejectionReason,
        extendedDueDate: dueDateObj.toISOString(),
      });

      const result = await fetchSubmissions();
      setSubmissions(result.submissions);
      setSubmissionCounts(result.counts);
      setTotalSubmissions(result.totalItems);
      if (result.subjectOptionsCandidate.length > 0) {
        setSubjectOptions((prev) =>
          normalizeOptions(result.subjectOptionsCandidate, prev)
        );
      }
      handleCloseRejectDialog();
      notify("Assignment rejected successfully.", "success");
    } catch (error) {
      console.error("Failed to reject submission:", error);
      notify(
        error?.response?.data?.message || error?.message || "Failed to reject submission. Please try again.",
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "submitted":
        return "info";
      case "graded":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const gradeDialogMeta = selectedSubmission
    ? getSubmissionEvaluationMeta(selectedSubmission)
    : {
        isMarksEvaluation: false,
        isGradeEvaluation: false,
        maxMarksValue: null,
        maxGradesValue: null,
      };
  const {
    isMarksEvaluation,
    isGradeEvaluation,
    maxMarksValue,
    maxGradesValue,
  } = gradeDialogMeta;

  const isGradeSubmitDisabled =
    actionLoading || (gradeInput ?? "").trim() === "";

  const submittedCount = submissionCounts.submitted ?? 0;
  const gradedCount = submissionCounts.graded ?? 0;
  const rejectedCount = submissionCounts.rejected ?? 0;

  const columns = [
    {
      field: "student",
      headerName: "Student",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: "primary.main",
              fontSize: "0.875rem",
            }}
          >
            {params.row.studentAvatar}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.studentName}
            </Typography>
            {!!String(params.row.rollNo || "").trim() && String(params.row.rollNo || "").trim() !== "-" && (
              <Typography variant="caption" color="text.secondary">
                {params.row.rollNo}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: "assignmentTitle",
      headerName: "Assignment",
      width: 250,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.subject}
          </Typography>
        </Box>
      ),
    },
    {
      field: "submittedOn",
      headerName: "Submitted",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {displayDate(params.value)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(params.value).format("hh:mm A")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "attemptCount",
      headerName: "Attempts",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ?? 1}
        </Typography>
      ),
    },
    {
      field: "onTime",
      headerName: "Timing",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "On Time" : "Late"}
          color={params.value ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "grade",
      headerName: "Grade",
      width: 120,
      renderCell: (params) => {
        if (params.row.status !== "graded") {
          return (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          );
        }

        const {
          isMarksEvaluation,
          isGradeEvaluation,
          maxMarksValue,
        } = getSubmissionEvaluationMeta(params.row);

        const rawValue = params.value ?? params.row.grade ?? params.row.score ?? "-";
        const displayValue =
          isMarksEvaluation && typeof rawValue === "number"
            ? rawValue
            : String(rawValue).toUpperCase();

        if (isGradeEvaluation) {
          return (
            <Typography variant="body2" fontWeight="bold" color="success.main">
              {displayValue}
            </Typography>
          );
        }

        const denominator =
          maxMarksValue ??
          params.row.maxMarks ??
          params.row.maxGrade ??
          "";

        return (
          <Typography variant="body2" fontWeight="bold" color="success.main">
            {displayValue}
            {denominator !== "" ? `/${denominator}` : ""}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewSubmission(params.row)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton
              size="small"
              color="primary"
              onClick={() => downloadSubmissionFile(params.row)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === "submitted" && (
            <>
              <Tooltip title="Grade">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleOpenGradeDialog(params.row)}
                >
                  <GradeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleOpenRejectDialog(params.row)}
                >
                  <RejectIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  const viewDialogStudentSubtitle = submissionViewSubtitle(selectedSubmission);

  return (
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Assignment Submissions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and grade student assignment submissions
            </Typography>
          </Box>
          {/* <Button variant="outlined" startIcon={<RefreshIcon />}>
            Refresh
          </Button> */}
        </Box>

        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToast((t) => ({ ...t, open: false }))}
            severity={toast.severity}
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        </Snackbar>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "info.light", color: "info.dark" }}>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}
              >
                <AssignmentIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {submittedCount}
                  </Typography>
                  <Typography variant="body2">Submitted</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "success.light", color: "success.dark" }}>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}
              >
                <GradeIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {gradedCount}
                  </Typography>
                  <Typography variant="body2">Graded</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "error.light", color: "error.dark" }}>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}
              >
                <RejectIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {rejectedCount}
                  </Typography>
                  <Typography variant="body2">Rejected</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  placeholder="Search by student or assignment"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 170 }} disabled={departmentLoading}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={departmentFilter}
                    label="Department"
                    onChange={(e) => {
                      setDepartmentFilter(e.target.value);
                      handleFilterChange("departmentId", e.target.value);
                    }}
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    {departmentOptions.map((department) => (
                      <MenuItem key={department.id} value={department.id}>
                        {department.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170 }} disabled={courseLoading}>
                  <InputLabel>Course</InputLabel>
                  <Select
                    value={courseFilter}
                    label="Course"
                    onChange={(e) => {
                      setCourseFilter(e.target.value);
                      handleFilterChange("courseId", e.target.value);
                    }}
                  >
                    <MenuItem value="all">All Courses</MenuItem>
                    {courseOptions.map((course) => (
                      <MenuItem key={course.id} value={course.id}>
                        {course.name}
                      </MenuItem>
                    ))}
                    {courseLoading && (
                      <MenuItem disabled>Loading courses...</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 190 }} disabled={classLoading}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={classFilter}
                    label="Class"
                    onChange={(e) => {
                      setClassFilter(e.target.value);
                      handleFilterChange("classId", e.target.value);
                    }}
                  >
                    <MenuItem value="all">All Classes</MenuItem>
                    {classOptions.map((classOption) => (
                      <MenuItem key={classOption.id} value={classOption.id}>
                        {classOption.name}
                      </MenuItem>
                    ))}
                    {classLoading && (
                      <MenuItem disabled>Loading classes...</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }} disabled={subjectLoading}>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    value={subjectFilter}
                    label="Subject"
                    onChange={(e) => {
                      setSubjectFilter(e.target.value);
                      handleFilterChange("subjectId", e.target.value);
                    }}
                  >
                    <MenuItem value="all">All Subjects</MenuItem>
                    {subjectOptions.map((subject) => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </MenuItem>
                    ))}
                    {subjectLoading && (
                      <MenuItem disabled>Loading subjects...</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label={`Submitted (${submittedCount})`} />
                <Tab label={`Graded (${gradedCount})`} />
                <Tab label={`Rejected (${rejectedCount})`} />
                <Tab label="All" />
              </Tabs>
            </Box>
          </CardContent>
        </Card>

        {/* Submissions Table */}
        <Card>
          <CardContent>
            <DataGrid
              rows={getFilteredSubmissions()}
              columns={columns}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => setPaginationModel(model)}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              rowCount={totalSubmissions}
              loading={submissionsLoading}
              autoHeight
              disableSelectionOnClick
            />
          </CardContent>
        </Card>

        {/* View Details Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={(e, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") return;
            setViewDialogOpen(false);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "primary.main" }}>
                {selectedSubmission?.studentAvatar}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {selectedSubmission?.studentName}
                </Typography>
                {viewDialogStudentSubtitle ? (
                  <Typography variant="body2" color="text.secondary">
                    {viewDialogStudentSubtitle}
                  </Typography>
                ) : null}
              </Box>
              <Chip
                label={selectedSubmission?.status?.toUpperCase()}
                color={getStatusColor(selectedSubmission?.status)}
                sx={{ ml: "auto" }}
              />
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedSubmission && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      {selectedSubmission.assignmentTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Subject: {selectedSubmission.subject} | Due:{" "}
                      {displayDate(selectedSubmission.dueDate)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Submission Details
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography variant="body2">
                        <strong>Submitted:</strong>{" "}
                        {dayjs(selectedSubmission.submittedOn).format(
                          "DD MMM YYYY, hh:mm A"
                        )}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Status:</strong>{" "}
                        <Chip
                          label={
                            selectedSubmission.onTime
                              ? "On Time"
                              : "Late Submission"
                          }
                          color={
                            selectedSubmission.onTime ? "success" : "error"
                          }
                          size="small"
                        />
                      </Typography>
                      <Typography variant="body2">
                        <strong>File:</strong> {selectedSubmission.file} (
                        {selectedSubmission.fileSize})
                      </Typography>
                      <Typography variant="body2">
                        <strong>Attempts:</strong>{" "}
                        {selectedSubmission.attemptCount ?? 1}
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        size="small"
                        sx={{ mt: 1, width: "fit-content" }}
                        onClick={handleDownloadSubmission}
                      >
                        Download Submission
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
                {selectedSubmission.status === "graded" && (
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, bgcolor: "success.light" }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Grading Details
                      </Typography>
                      {(() => {
                        const {
                          isMarksEvaluation: viewIsMarks,
                          isGradeEvaluation: viewIsGrade,
                          maxMarksValue: viewMaxMarks,
                          maxGradesValue: viewMaxGrades,
                        } = getSubmissionEvaluationMeta(selectedSubmission);

                        const rawGradeValue =
                          selectedSubmission.grade ?? selectedSubmission.score ?? "-";
                        const displayGradeValueRaw =
                          typeof rawGradeValue === "number"
                            ? rawGradeValue
                            : String(rawGradeValue);

                        const displayGradeValue =
                          viewIsMarks && typeof displayGradeValueRaw === "number"
                            ? displayGradeValueRaw
                            : String(displayGradeValueRaw).toUpperCase();

                        const numericGrade = Number(displayGradeValue);
                        const hasNumericGrade =
                          !Number.isNaN(numericGrade) && viewIsMarks;

                        const denominatorCandidate =
                          viewMaxMarks ?? selectedSubmission.maxMarks ?? selectedSubmission.maxGrade;
                        const numericDenominator = Number(denominatorCandidate);
                        const hasNumericDenominator =
                          !Number.isNaN(numericDenominator) && numericDenominator > 0;

                        return (
                          <>
                            {viewIsMarks && denominatorCandidate !== undefined && denominatorCandidate !== null && denominatorCandidate !== "" && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="h4"
                                  fontWeight="bold"
                                  color="success.dark"
                                >
                                  {displayGradeValue} / {denominatorCandidate}
                                </Typography>
                                {hasNumericGrade && hasNumericDenominator && (
                                  <Rating
                                    value={(numericGrade / numericDenominator) * 5}
                                    readOnly
                                    precision={0.5}
                                  />
                                )}
                              </Box>
                            )}
                            {viewIsGrade && (
                              <Typography
                                variant="h4"
                                fontWeight="bold"
                                color="success.dark"
                                sx={{ textTransform: "uppercase", mb: 1 }}
                              >
                                {displayGradeValue}
                              </Typography>
                            )}
                            {viewIsGrade && viewMaxGrades && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                Maximum Grade: {viewMaxGrades}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                      {selectedSubmission.feedback && (
                        <Typography variant="body2">
                          <strong>Feedback:</strong>{" "}
                          {selectedSubmission.feedback}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Graded by {selectedSubmission.gradedBy} on{" "}
                        {dayjs(selectedSubmission.gradedOn).format(
                          "DD MMM YYYY"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {selectedSubmission.status === "rejected" && (
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, bgcolor: "error.light" }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Rejection Details
                      </Typography>
                      <Typography variant="body2">
                        <strong>Reason:</strong>{" "}
                        {selectedSubmission.rejectionReason}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rejected BY {selectedSubmission.rejectedBy} on{" "}
                        {dayjs(selectedSubmission.rejectedOn).format(
                          "DD MMM YYYY"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            {selectedSubmission?.status === "pending" && (
              <>
                <Button
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleOpenRejectDialog(selectedSubmission);
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<GradeIcon />}
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleOpenGradeDialog(selectedSubmission);
                  }}
                >
                  Grade
                </Button>
              </>
            )}
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Grade Dialog */}
        <Dialog
          open={gradeDialogOpen}
          onClose={(e, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") return;
            setGradeDialogOpen(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GradeIcon color="success" />
              Grade Assignment
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Grading submission from{" "}
                <strong>{selectedSubmission?.studentName}</strong> (
                {selectedSubmission?.rollNo})
              </Alert>
              <TextField
                label={
                  isGradeEvaluation
                    ? `Grade${maxGradesValue ? ` (Max ${maxGradesValue})` : ""}`
                    : `Score${
                        maxMarksValue !== null && maxMarksValue !== undefined
                          ? ` (out of ${maxMarksValue})`
                          : ""
                      }`
                }
                type={isMarksEvaluation ? "number" : "text"}
                fullWidth
                value={gradeInput}
                onChange={(event) => {
                  const { value } = event.target;
                  if (isMarksEvaluation) {
                    const numericPattern = /^\d*(\.\d{0,2})?$/;
                    if (value === "" || numericPattern.test(value)) {
                      setGradeInput(value);
                    }
                  } else if (isGradeEvaluation) {
                    const sanitized = value.toUpperCase().replace(/[^A-Z]/g, "");
                    setGradeInput(sanitized.slice(0, 1));
                  } else {
                    setGradeInput(value);
                  }
                }}
                InputProps={{
                  endAdornment:
                    isMarksEvaluation &&
                    (maxMarksValue !== null && maxMarksValue !== undefined) ? (
                      <InputAdornment position="end">
                        / {maxMarksValue}
                      </InputAdornment>
                    ) : undefined,
                }}
                sx={{ mb: 2 }}
                inputProps={
                  isMarksEvaluation
                    ? { min: 0, step: "0.01", inputMode: "decimal" }
                    : { maxLength: 1, pattern: "[A-Za-z]" }
                }
                placeholder={
                  isGradeEvaluation
                    ? "Enter grade (e.g. A)"
                    : "Enter score"
                }
              />
              {isMarksEvaluation ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  Enter numeric score only. Decimals up to two places are allowed.
                </Typography>
              ) : isGradeEvaluation ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  Enter a single letter grade (A–Z).
                </Typography>
              ) : null}
              <TextField
                label="Feedback (Optional)"
                multiline
                rows={4}
                fullWidth
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Provide constructive feedback for the student..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGradeDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleGradeSubmission}
              disabled={isGradeSubmitDisabled}
              startIcon={<SendIcon />}
            >
              Submit Grade
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog
          open={rejectDialogOpen}
          onClose={(e, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") return;
            handleCloseRejectDialog();
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RejectIcon color="error" />
              Reject Assignment
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                You are about to reject the submission from{" "}
                <strong>{selectedSubmission?.studentName}</strong>. The student
                will be notified and asked to resubmit.
              </Alert>
              <TextField
                label="Rejection Reason"
                multiline
                rows={4}
                fullWidth
                required
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why the submission is being rejected..."
              />
              <DatePicker
                label="Extended Due Date"
                value={extendedDueDate}
                onChange={(newValue) => setExtendedDueDate(newValue)}
                format={DATE_DISPLAY_FORMAT}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    sx: { mt: 2 },
                    inputProps: { inputMode: "numeric", autoComplete: "off" },
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRejectDialog} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRejectSubmission}
              disabled={!rejectionReason || !extendedDueDate || actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Reject Submission"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}

export default AssignmentApproval;
